import { useEffect, useRef } from "react";
import { VIEWSPEC_CODES } from "../viewspecs/codes.ts";
import { getAllCommands } from "../editor/commands/index.ts";

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const VIEWSPEC_DESCRIPTIONS: Record<string, string> = {
  a: "All levels (no depth limit)",
  b: "Branch tops only (depth 1)",
  c: "Two levels deep",
  d: "Three levels deep",
  e: "Show all content",
  f: "First line only",
  g: "No content (titles only)",
  h: "Show statement IDs",
  i: "Hide statement IDs",
  j: "Hierarchical numbering (SID)",
  k: "Sequential numbering (1, 2, 3...)",
  l: "No numbering",
  m: "Indented view",
  n: "Flat view (no indentation)",
  o: "Spaced (blank lines between)",
  p: "Compact (no blank lines)",
  q: "Horizontal split",
  r: "Vertical split",
  s: "No split (single pane)",
  t: "Link split panes",
  u: "Unlink split panes",
};

const KEYBINDINGS = [
  { key: "/", description: "Open command bar" },
  { key: "Escape", description: "Close command bar / cancel" },
  { key: "?", description: "Toggle this help dialog" },
  { key: "Ctrl+Shift+V", description: "Toggle viewspec panel" },
  { key: "Ctrl+J", description: "Jump to statement" },
  { key: "Ctrl+G", description: "Go to document" },
  { key: "Ctrl+F", description: "Filter statements" },
  { key: "Alt+Left", description: "Navigate back" },
  { key: "Alt+Right", description: "Navigate forward" },
  { key: "Enter", description: "Split statement at cursor (new statement)" },
  { key: "Tab", description: "Indent statement (increase level)" },
  { key: "Shift+Tab", description: "Outdent statement (decrease level)" },
  { key: "Ctrl+\\", description: "Split screen" },
  { key: "Ctrl+]", description: "Zoom in (freeze on statement)" },
  { key: "Ctrl+[", description: "Zoom out (unfreeze)" },
  { key: "Ctrl+Shift+1", description: "Set depth to 1" },
  { key: "Ctrl+Shift+2", description: "Set depth to 2" },
  { key: "Ctrl+Shift+3", description: "Set depth to 3" },
  { key: "Ctrl+Shift+0", description: "Show all depths" },
];

export function HelpDialog({ open, onClose }: HelpDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "?") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const commands = getAllCommands();

  return (
    <div className="help-overlay">
      <div className="help-dialog" ref={dialogRef}>
        <div className="help-header">
          <h2>AUGMENT / NLS Reference</h2>
          <button onClick={onClose} className="help-close">
            ESC
          </button>
        </div>

        <div className="help-content">
          {/* Viewspec Codes */}
          <section>
            <h3>Viewspec Codes</h3>
            <p className="help-hint">
              Type <kbd>:codes</kbd> in the command bar to apply. E.g.{" "}
              <kbd>:bhm</kbd> = depth 1 + SIDs + indented
            </p>
            <div className="help-grid viewspec-grid">
              {Object.keys(VIEWSPEC_CODES).map((code) => (
                <div key={code} className="help-row">
                  <kbd>{code}</kbd>
                  <span>{VIEWSPEC_DESCRIPTIONS[code] ?? ""}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Commands */}
          <section>
            <h3>Commands</h3>
            <p className="help-hint">
              Press <kbd>/</kbd> to open the command bar, then type a command
              name or alias
            </p>
            <div className="help-grid command-grid">
              {commands.map((cmd) => (
                <div key={cmd.name} className="help-row">
                  <div>
                    <kbd>{cmd.name}</kbd>
                    {cmd.aliases.length > 0 && (
                      <span className="help-aliases">
                        {cmd.aliases.map((a) => (
                          <kbd key={a}>{a}</kbd>
                        ))}
                      </span>
                    )}
                  </div>
                  <span>{cmd.description}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Keybindings */}
          <section>
            <h3>Keyboard Shortcuts</h3>
            <div className="help-grid keybinding-grid">
              {KEYBINDINGS.map((kb) => (
                <div key={kb.key} className="help-row">
                  <kbd>{kb.key}</kbd>
                  <span>{kb.description}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
